/* HowWeHelpWritersPage — secondary marketing surface (2026-02).
 *
 * Split from the primary landing (`/`) so the root can focus on
 * conversion. Every "why choose us" section lives here — AI Offerings,
 * Trust badges, OHAS D3 explainer, live Activity Ticker, Authors'
 * Portal, and (2026-02 refresh) a testimonial strip pulled straight
 * from admin-editable landing content.
 *
 * All hero + AI offerings + testimonial copy is read from the same
 * `landing` config that powers `/`, so marketing tweaks ship without
 * a redeploy via Admin → Landing → Content → "How We Help Writers
 * page".
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, KeyRound, Sparkles, Quote, PenTool, Tag } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useSeoMeta } from '@/lib/useSeoMeta';
import { useHreflang } from '@/lib/useHreflang';   // 2026-02 · hreflang for all 6 locales
import { landingApi } from '@/lib/landingApi';
import { buildDefaultsDoc } from '@/components/landing/landingDefaults';
import { trackLandingEvent } from '@/lib/landingAnalytics';
import TestimonialSilhouette from '@/components/landing/TestimonialSilhouette';

import WritersSalonModal from '@/components/landing/scriptorium/WritersSalonModal';
import LandingPricingDialog from '@/components/landing/LandingPricingDialog';
import AuthorsPortalSection from '@/components/landing/AuthorsPortalSection';
import LandingAuthorTicketRail from '@/components/landing/LandingAuthorTicketRail';
import { ActivityTicker } from '@/components/landing/ActivityTicker';
import PoweredByEmergent from '@/components/landing/PoweredByEmergent';
import AudiobookSamplePlayer from '@/components/landing/scriptorium/AudiobookSamplePlayer';
import PublicNavBar from '@/components/landing/PublicNavBar';
import { P, FONTS, GRAIN_URL, useScriptoriumFonts } from '@/components/landing/scriptorium/palette';
import StructuredData from '@/components/seo/StructuredData';

const SEO_FALLBACK = {
  title: 'AI Novel Writing Software for Indie Authors — Crown & Rose',
  description: 'AI novel writing software built for indie authors who finish. Multi-model AI drafting (Claude, GPT, Gemini), guided KDP publishing, 85% bookstore royalties, and OHAS D3 human-authorship certification. No per-generation fees.',
  keywords: 'AI novel writing software, novel writing AI, AI book writing tool, indie author software, AI novel writer, KDP publishing software, novel writing app, fiction writing AI, scrivener alternative, AI writing studio for authors, Crown and Rose Loom',
  og_title: 'AI Novel Writing Software — Crown & Rose',
  og_description: 'Multi-model AI drafting, guided KDP publishing, and 85% bookstore royalties. The writing studio built for indie authors who take their craft seriously.',
  og_image: 'https://static.prod-images.emergentagent.com/jobs/67d45115-9fac-4a11-82ed-b7a8bff08f0b/images/c8c06a0d48fb82f7ad11dad68d299c956758538311895d667b50c6002f055f92.jpeg',
  robots: 'index, follow',
};

/** Split the pipe/newline-encoded testimonials string into
 * `{ quote, author, book, avatar, presetIdx }` rows. Rows missing a
 * quote are dropped. The 4th column is either:
 *   * a URL — rendered as a real portrait, or
 *   * `@preset-N` (N = 1..6) — rendered as one of the built-in
 *     sepia silhouettes so admins can pin a face without hosting an
 *     image, or
 *   * empty — a name-hashed silhouette is picked automatically. */
function parseTestimonials(raw) {
  if (!raw || typeof raw !== 'string') return [];
  return raw.split('\n').map((line) => {
    const [quote, author, book, avatarCol] = line.split('|').map((s) => (s || '').trim());
    let avatar = '';
    let presetIdx;
    if (avatarCol) {
      const m = /^@preset-(\d+)$/i.exec(avatarCol);
      if (m) presetIdx = Math.max(0, parseInt(m[1], 10) - 1);
      else   avatar    = avatarCol;
    }
    return { quote, author, book, avatar, presetIdx };
  }).filter((r) => r.quote);
}

function splitBullets(raw) {
  if (!raw || typeof raw !== 'string') return [];
  return raw.split('\n').map((s) => s.trim()).filter(Boolean);
}

export default function HowWeHelpWritersPage() {
  useSeoMeta('/how-we-help-writers', SEO_FALLBACK);
  useHreflang('how-we-help-writers');   // injects hreflang for all 6 locale variants
  useScriptoriumFonts();
  const { t, i18n } = useTranslation();
  const isEnglish = (i18n.language || 'en').toLowerCase().startsWith('en');
  const cms = (cmsVal, fallbackKey) => (isEnglish && cmsVal) ? cmsVal : t(fallbackKey);

  const [emailOpen, setEmailOpen] = useState(false);
  const [salonMode, setSalonMode] = useState('signup');
  const [pricingOpen, setPricingOpen] = useState(false);
  const [doc, setDoc] = useState(buildDefaultsDoc());

  useEffect(() => {
    trackLandingEvent('hwh_view');
    let alive = true;
    landingApi.get()
      .then((d) => { if (alive) setDoc((prev) => ({ ...prev, ...d })); })
      .catch(() => { /* keep defaults */ });
    return () => { alive = false; };
  }, []);

  const siteUrl = typeof window !== 'undefined' ? window.location.origin : 'https://crownnrose.com';
  const breadcrumbData = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type':    'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home',                item: `${siteUrl}/` },
      { '@type': 'ListItem', position: 2, name: 'How We Help Writers', item: `${siteUrl}/how-we-help-writers` },
    ],
  }), [siteUrl]);

  const content = doc.content || {};
  const bullets = useMemo(() => splitBullets(content.hwh_ai_offerings_bullets), [content.hwh_ai_offerings_bullets]);
  const testimonials = useMemo(() => parseTestimonials(content.hwh_testimonials), [content.hwh_testimonials]);

  const openSignup = (source) => {
    trackLandingEvent('hwh_start_trial_click', source ? { source } : undefined);
    setSalonMode('signup'); setEmailOpen(true);
  };
  const openSignin = (source) => {
    trackLandingEvent('hwh_signin_click', source ? { source } : undefined);
    setSalonMode('signin'); setEmailOpen(true);
  };
  const openPlans = () => {
    trackLandingEvent('hwh_plans_click');
    setPricingOpen(true);
  };

  return (
    <div
      className="relative h-screen overflow-y-auto"
      style={{ backgroundColor: P.paper, color: P.ink, fontFamily: FONTS.body }}
      data-testid="how-we-help-page"
    >
      <StructuredData id="breadcrumbs-help" data={breadcrumbData} />
      <div className="pointer-events-none fixed inset-0 z-0 opacity-30" style={{ backgroundImage: GRAIN_URL }} />

      {/* 2026-02 · Left-rail nav on the marketing pages too, so
       *  visitors keep the Desk / Vault / Margin / FAQ / Find
       *  shortcuts wherever they land. */}


      {/* Unified public nav — shared across all marketing sub-pages. */}
      <PublicNavBar
        onSignIn={() => openSignin('header')}
        onSignUp={() => openSignup('header')}
      />

      {/* 2026-02 (moved from `/`) — the audiobook sample + Bookstore/
       * Loom/plans CTA row + "ready to start" invite banner now open
       * this page, so a visitor hears/sees the result before reading
       * the pitch below. Copy on the invite banner is reworded for
       * this page's context — it used to invite people FROM elsewhere
       * TO this page; now that it lives here, it leads straight into
       * signup instead of linking back to itself. */}
      <div className="relative z-10 px-6 pb-6 pt-8">
        <AudiobookSamplePlayer />
      </div>

      <section className="relative z-10 mx-auto max-w-4xl px-6 pb-8">
        <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-center">
          <button
            type="button"
            onClick={() => openSignup('cta-row')}
            data-testid="hwh-cta-open-loom"
            className="group inline-flex items-center justify-center gap-2 rounded-md px-5 py-3 text-[13px] font-semibold transition hover:shadow-md sm:w-auto"
            style={{ backgroundColor: P.oxblood, color: P.paper, fontFamily: FONTS.display }}
          >
            <PenTool className="h-4 w-4" /> {t('landing.ctaPrimary')}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </button>
          <button
            type="button"
            onClick={openPlans}
            data-testid="hwh-cta-see-plans"
            className="group inline-flex items-center justify-center gap-2 rounded-md border px-5 py-3 text-[13px] font-semibold transition hover:shadow-md sm:w-auto"
            style={{ borderColor: `${P.gold}88`, color: P.oxblood, backgroundColor: P.paper, fontFamily: FONTS.display }}
          >
            <Tag className="h-4 w-4" /> {t('landing.seeAllPlans')}
            <ArrowRight className="h-4 w-4 opacity-70 transition-transform group-hover:translate-x-0.5" />
          </button>
        </div>
      </section>


      {/* Hero */}
      <section className="relative z-10 mx-auto max-w-4xl px-6 pt-12 pb-6 text-center"
                data-testid="hwh-hero">
        <div>
          <span
            className="inline-block rounded-full px-4 py-1.5 text-[11.5px] uppercase tracking-[0.28em]"
            style={{ backgroundColor: P.oxblood, color: P.paper,
                      fontFamily: FONTS.display, fontWeight: 700 }}
            data-testid="hwh-hero-eyebrow"
          >
            {content.hwh_hero_eyebrow || 'Home for Indie Authors'}
          </span>
        </div>
        <h1
          className="mx-auto mt-4 max-w-3xl text-lg leading-tight sm:text-2xl lg:text-3xl"
          style={{ fontFamily: FONTS.display, fontWeight: 600, color: P.ink }}
          data-testid="hwh-hero-title"
        >
          {content.hwh_hero_title || 'How Crown & Rose helps writers build a business.'}
        </h1>
        <p
          className="mx-auto mt-4 max-w-2xl text-base sm:text-lg leading-relaxed"
          style={{ color: P.inkSoft, fontFamily: FONTS.serif }}
          data-testid="hwh-hero-body"
        >
          {content.hwh_hero_body ||
            'Multi-model AI drafting, a full royalty & marketing dashboard, direct-to-reader storefront, and OHAS D3 human-authorship certification — everything a serious indie novelist needs, under one roof.'}
        </p>
      </section>

      {/* AI offerings */}
      <section className="relative z-10 mx-auto max-w-4xl px-6 py-6"
                data-testid="hwh-craft-multiplier">
        <div
          className="grid gap-4 rounded-lg border p-5 sm:grid-cols-[2fr_1fr] sm:p-6"
          style={{ borderColor: `${P.gold}55`, backgroundColor: P.paperDeep }}
        >
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.2em]" style={{ color: P.inkSoft }}>
              AI offerings
            </div>
            <h2 className="mt-1 text-lg font-semibold leading-snug sm:text-xl"
                 style={{ fontFamily: FONTS.display, color: P.ink }}
                 data-testid="hwh-ai-headline">
              {content.hwh_ai_offerings_headline || 'AI assist for copywriters and creatives.'}
            </h2>
            <p className="mt-2 text-[13.5px] leading-relaxed" style={{ color: P.inkSoft }}
                data-testid="hwh-ai-body">
              {content.hwh_ai_offerings_body ||
                'Writers are assisted by AI for copywriter-able creations; creatives may also find the Craft Multiplier useful for content in the bookstore under AI offerings.'}
            </p>
            {bullets.length > 0 && (
              <ul className="mt-3 space-y-1 text-[13px]" style={{ color: P.ink }}
                   data-testid="hwh-ai-bullets">
                {bullets.map((b, i) => (
                  <li key={i} data-testid={`hwh-ai-bullet-${i}`}>• {b}</li>
                ))}
              </ul>
            )}
          </div>
          <Link
            to="/faq"
            data-testid="hwh-faq-cta"
            onClick={() => trackLandingEvent('hwh_faq_click')}
            className="flex flex-col justify-center rounded-md border px-4 py-3 text-[12.5px] font-semibold transition hover:shadow-md"
            style={{ borderColor: `${P.gold}88`, color: P.ink, backgroundColor: P.paper }}
          >
            <span className="text-[10px] uppercase tracking-[0.18em]" style={{ color: P.inkSoft }}>
              New here?
            </span>
            <span className="mt-1">
              Every question about pricing, copyright, translation, and the Craft
              Multiplier answered on one page.
            </span>
            <span className="mt-2 text-[11.5px]" style={{ color: P.gold }}>
              Read the full FAQ →
            </span>
          </Link>
        </div>
      </section>

      {/* Trust badges */}
      <section className="relative z-10 mx-auto max-w-4xl px-6 py-6"
                data-testid="hwh-trust-badges">
        <div className="grid gap-3 sm:grid-cols-2">
          <div
            className="rounded-lg border p-5"
            style={{ borderColor: `${P.gold}66`, backgroundColor: P.paperDeep }}
            data-testid="hwh-trust-badge-integrity"
          >
            <div className="mb-1 text-[10.5px] uppercase tracking-[0.22em]" style={{ color: P.oxblood }}>
              Promise
            </div>
            <div className="text-[15px] font-semibold" style={{ fontFamily: FONTS.display, color: P.ink }}>
              {content.trust_badge_title || 'Trust & Integrity'}
            </div>
            <p className="mt-2 text-[13px] leading-relaxed" style={{ color: P.inkSoft }}>
              {content.trust_badge_body ||
                "If a novel is written by a person or produced with our Content Multiplier, it will be clearly marked on the shelf and inside every book's provenance record — no exceptions, no fine print."}
            </p>
          </div>
          <div
            className="rounded-lg border p-5"
            style={{ borderColor: `${P.gold}66`, backgroundColor: P.paperDeep }}
            data-testid="hwh-trust-badge-reader"
          >
            <div className="mb-1 text-[10.5px] uppercase tracking-[0.22em]" style={{ color: P.oxblood }}>
              How we earn it
            </div>
            <div className="text-[15px] font-semibold" style={{ fontFamily: FONTS.display, color: P.ink }}>
              {content.reader_badge_title || 'Building Trust — One Reader at a Time'}
            </div>
            <p className="mt-2 text-[13px] leading-relaxed" style={{ color: P.inkSoft }}>
              {content.reader_badge_body ||
                '85% of every direct sale goes back to the writer from our bookstore — more per copy than Amazon KDP even at our 90% pass-through on their channel. Every book carries a downloadable Editing Record so readers can see exactly how it was made. That is how a bookstore earns loyalty — one honest transaction at a time.'}
            </p>
          </div>
        </div>
      </section>

      {/* 2026-02 · Royalty/business-pitch paragraphs relocated here
       *  from the main landing hero (/) — they read as supporting
       *  detail right after the Promise / How We Earn It badges,
       *  rather than hero-page clutter. Same CMS fields as before. */}
      {(content.hero_paragraph_3 || content.hero_paragraph_4_large || content.hero_business_tagline) && (
        <section className="relative z-10 mx-auto max-w-3xl px-6 pb-4 text-center" data-testid="hwh-royalty-paragraphs">
          {content.hero_paragraph_3 && (
            <p className="text-base sm:text-lg leading-relaxed" style={{ color: P.inkSoft, fontFamily: FONTS.serif }}>
              {content.hero_paragraph_3}
            </p>
          )}
          {content.hero_paragraph_4_large && (
            <p className="mx-auto mt-4 max-w-2xl text-xl sm:text-2xl leading-snug" style={{ color: P.ink, fontFamily: FONTS.display, fontWeight: 500 }}>
              {content.hero_paragraph_4_large.split('\n').map((line, i, arr) => (
                <React.Fragment key={i}>
                  {line}
                  {i < arr.length - 1 && <br />}
                </React.Fragment>
              ))}
            </p>
          )}
          {content.hero_business_tagline && (
            <p className="mt-4 text-base sm:text-lg leading-relaxed" style={{ color: P.oxblood, fontFamily: FONTS.serif, fontWeight: 600 }}>
              {content.hero_business_tagline}
            </p>
          )}
        </section>
      )}

      {/* Testimonials — 2026-02 refresh. Admin-editable pipe-separated
       *  rows: "quote|author|book". Auto-hides when empty. */}
      {testimonials.length > 0 && (
        <section className="relative z-10 mx-auto max-w-5xl px-6 py-8"
                  data-testid="hwh-testimonials">
          <div className="mb-4 text-center">
            <div className="text-[10.5px] uppercase tracking-[0.28em]"
                  style={{ color: P.oxblood, fontFamily: FONTS.type }}>
              What indie authors say
            </div>
            <h2 className="mt-1 text-2xl sm:text-3xl"
                 style={{ fontFamily: FONTS.display, fontWeight: 600, color: P.ink }}>
              Words from writers already on The Loom
            </h2>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {testimonials.map((t, i) => (
              <figure
                key={i}
                data-testid={`hwh-testimonial-${i}`}
                className="rounded-lg border p-5"
                style={{ borderColor: `${P.gold}66`, backgroundColor: P.paperDeep }}
              >
                <Quote className="h-4 w-4" style={{ color: P.oxblood }} />
                <blockquote className="mt-2 text-[13.5px] leading-relaxed"
                              style={{ color: P.ink, fontFamily: FONTS.serif }}>
                  “{t.quote}”
                </blockquote>
                <figcaption className="mt-3 flex items-center gap-2 text-[11px]"
                              style={{ color: P.inkSoft }}>
                  {t.avatar ? (
                    <img
                      src={t.avatar}
                      alt=""
                      aria-hidden
                      data-testid={`hwh-testimonial-${i}-avatar`}
                      className="h-8 w-8 flex-shrink-0 rounded-full border object-cover"
                      style={{ borderColor: `${P.gold}88` }}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      draggable={false}
                    />
                  ) : (
                    <TestimonialSilhouette
                      name={t.author || `testimonial-${i}`}
                      presetIdx={t.presetIdx}
                      size={32}
                      testId={`hwh-testimonial-${i}-silhouette`}
                    />
                  )}
                  <span>
                    <span className="font-semibold" style={{ color: P.ink }}>{t.author}</span>
                    {t.book && <span className="italic"> · {t.book}</span>}
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>
      )}

      {/* OHAS D3 */}
      <section className="relative z-10 mx-auto max-w-4xl px-6 py-6"
                data-testid="hwh-ohas">
        <div className="rounded-lg border p-5"
              style={{ borderColor: `${P.gold}55`, backgroundColor: P.paperDeep }}>
          <div className="flex flex-wrap items-center gap-2">
            <span
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
              style={{ backgroundColor: '#8b5cf6', color: '#fff' }}
            >
              OHAS · D3
            </span>
            <span className="text-[15px] font-semibold" style={{ fontFamily: FONTS.display, color: P.ink }}>
              Open Human Authorship Standards — D3 (Formal Audit)
            </span>
          </div>
          <p className="mt-2 text-[13px] leading-relaxed" style={{ color: P.inkSoft }}>
            Every book published by Crown &amp; Rose ships with a signed OHAS D3
            manifest, aligned with the U.S. Copyright Office human-authorship
            requirements for AI-assisted literary works. Readers can verify each
            book's provenance directly from the shelf.
          </p>
          <Link
            to="/faq#ohas"
            data-testid="hwh-ohas-learn"
            className="mt-3 inline-block rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-wider hover:opacity-80"
            style={{ borderColor: P.gold, color: P.oxblood }}
          >
            Learn more →
          </Link>
        </div>
      </section>

      <ActivityTicker accent={P.oxblood} cardBg={P.paperDeep} borderSoft={`${P.gold}88`} />
      <AuthorsPortalSection onOpenPortal={() => openSignin('authors-portal')} />
      <div className="relative z-10">
        <LandingAuthorTicketRail />
      </div>

      {/* ─── Pricing overview ─────────────────────────────────────────────── */}
      <section
        className="relative z-10 mx-auto max-w-4xl px-6 py-10"
        data-testid="hwh-pricing-overview"
      >
        <div
          className="rounded-lg border p-6"
          style={{ borderColor: `${P.gold}66`, backgroundColor: P.paperDeep }}
        >
          <div className="mb-1 text-[10.5px] uppercase tracking-[0.22em]" style={{ color: P.oxblood }}>
            Plans &amp; Pricing
          </div>
          <h2
            className="text-xl font-semibold leading-snug"
            style={{ fontFamily: FONTS.display, color: P.ink }}
            data-testid="hwh-pricing-title"
          >
            Four tiers. Two billing modes. One platform.
          </h2>
          <p className="mt-2 text-[13px] leading-relaxed" style={{ color: P.inkSoft }}>
            Every paid plan runs on the same Character Bible, Arc Visualizer, Edit Walker, and
            multi-model drafting engine. The tiers adjust how many AI tokens you get each month
            and which human-editorial services are layered on top.
          </p>

          {/* 4-tier comparison strip */}
          <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="hwh-pricing-tier-strip">
            {[
              { name: 'Scribe',    price: '$14',  byokRequired: true,  tokens: '0 platform tokens' },
              { name: 'Author',    price: '$39',  byok: '$25',          tokens: '1.5 M tokens/mo'  },
              { name: 'Publisher', price: '$119', byok: '$79',          tokens: '4.5 M tokens/mo'  },
              { name: 'Studio',    price: '$449', byok: '$299',         tokens: '20 M tokens/mo'   },
            ].map((tier) => (
              <div
                key={tier.name}
                className="flex flex-col rounded-md border p-3 text-center"
                style={{ borderColor: `${P.gold}55`, backgroundColor: P.paper }}
              >
                <span className="text-[11px] font-semibold" style={{ fontFamily: FONTS.display, color: P.ink }}>
                  {tier.name}
                </span>
                <span className="mt-1 font-mono text-[12px] font-bold" style={{ color: P.oxblood }}>
                  {tier.price}<span className="text-[9px] font-normal" style={{ color: P.inkSoft }}>/mo</span>
                </span>
                {tier.byokRequired ? (
                  <span className="mt-1 text-[9.5px] font-semibold uppercase tracking-wide" style={{ color: P.oxblood }}>
                    BYOK required
                  </span>
                ) : (
                  <>
                    <span className="text-[10px]" style={{ color: P.inkSoft }}>standard</span>
                    <div className="mt-1.5 rounded-sm px-1 py-0.5 text-[9.5px] font-semibold" style={{ backgroundColor: `${P.gold}22`, color: P.oxblood }}>
                      {tier.byok}/mo BYOK
                    </div>
                  </>
                )}
                <div className="mt-2 rounded-sm px-1 py-0.5 text-[9px]" style={{ backgroundColor: `${P.gold}11`, color: P.inkSoft }}>
                  {tier.tokens}
                </div>
              </div>
            ))}
          </div>

          {/* BYOK explanation */}
          <div
            className="mt-4 rounded-md border-l-4 py-2 pl-3 text-[12px] leading-relaxed"
            style={{ borderColor: P.gold, color: P.inkSoft }}
            data-testid="hwh-byok-explainer"
          >
            <strong style={{ color: P.ink }}>BYOK — Bring Your Own Key:</strong>{' '}
            Paste a personal OpenAI, Anthropic, or Gemini key in Settings → AI Keys. Every draft
            that uses that provider bills <em>your</em> account directly at cost price — we charge
            only the reduced platform fee shown above. Writers with existing API credits save up
            to&nbsp;<strong style={{ color: P.oxblood }}>36% on the monthly fee</strong>.
            All tiers also available with <strong style={{ color: P.ink }}>annual billing at −20%</strong> (e.g. Author standard $374.40/yr · $31.20/mo).
          </div>

          <button
            type="button"
            onClick={openPlans}
            data-testid="hwh-pricing-see-plans-btn"
            className="mt-4 inline-flex items-center gap-2 rounded-md px-4 py-2 text-[12.5px] font-semibold transition hover:shadow-sm"
            style={{ backgroundColor: P.oxblood, color: P.paper, fontFamily: FONTS.display }}
          >
            Compare all plans <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </section>

      {/* Bottom conversion CTA */}
      <section
        className="relative z-10 mx-auto max-w-4xl px-6 py-12 text-center"
        data-testid="hwh-bottom-cta"
      >
        <h2
          className="text-3xl sm:text-4xl leading-tight"
          style={{ fontFamily: FONTS.display, fontWeight: 600, color: P.ink }}
          data-testid="hwh-bottom-cta-title"
        >
          {content.hwh_bottom_cta_title || 'Ready to try The Loom?'}
        </h2>
        <p className="mx-auto mt-2 max-w-xl text-[13.5px] leading-relaxed"
            style={{ color: P.inkSoft, fontFamily: FONTS.serif }}
            data-testid="hwh-bottom-cta-body">
          {content.hwh_bottom_cta_body ||
            'Free to get started — full manuscript access, character bibles, multi-model drafting, and direct-to-reader sales at 85%. No card required.'}
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button" onClick={() => openSignup('bottom-cta')} data-testid="hwh-cta-start-trial"
            className="group inline-flex items-center gap-2 rounded-md px-5 py-3 text-[13px] font-semibold transition hover:shadow-md"
            style={{ backgroundColor: P.oxblood, color: P.paper, fontFamily: FONTS.display }}
          >
            <Sparkles className="h-4 w-4" /> Start writing free — no card required
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </button>
          <button
            type="button" onClick={() => openSignin('bottom-cta')} data-testid="hwh-cta-signin"
            className="group inline-flex items-center gap-2 rounded-md border px-5 py-3 text-[13px] font-semibold transition"
            style={{ borderColor: P.oxblood, color: P.oxblood, backgroundColor: P.paperDeep, fontFamily: FONTS.display }}
          >
            <KeyRound className="h-4 w-4" /> Email sign-in
          </button>
          <button
            type="button" onClick={openPlans} data-testid="hwh-cta-plans"
            className="inline-flex items-center gap-2 rounded-md border px-5 py-3 text-[13px] font-semibold transition"
            style={{ borderColor: `${P.gold}88`, color: P.oxblood, backgroundColor: P.paper, fontFamily: FONTS.display }}
          >
            See all plans
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer
        className="relative z-10 border-t px-6 py-4 text-[10.5px]"
        style={{ borderColor: `${P.gold}55`, backgroundColor: P.paperDeep, color: P.inkSoft }}
      >
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <div>© Crown &amp; Rose · All rights reserved.</div>
          <Link to="/" data-testid="hwh-footer-home"
                className="opacity-80 hover:opacity-100"
                style={{ color: P.inkSoft, fontFamily: FONTS.display, letterSpacing: '0.14em' }}>
            Home
          </Link>
        </div>
        <PoweredByEmergent testId="hwh-powered-by-emergent" />
      </footer>

      <WritersSalonModal
        open={emailOpen}
        initialMode={salonMode}
        onClose={() => setEmailOpen(false)}
      />
      <LandingPricingDialog
        open={pricingOpen}
        onOpenChange={setPricingOpen}
        onSelect={() => { setPricingOpen(false); openSignup('pricing-dialog'); }}
      />
    </div>
  );
}
